"use client";

import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ClockIcon,
  EyeIcon,
  MoonIcon,
  RocketIcon,
  SearchIcon,
  SunIcon,
  type LucideIcon,
} from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { accentText, ctaButton, focusRing } from "@/components/landing/cta-styles";
import { IntegrationsMarquee } from "@/components/landing/integrations-marquee";
import { LandingCta } from "@/components/landing/landing-cta";
import { prefersReducedMotion } from "@/components/landing/reduced-motion";
import { TESTIMONIALS } from "@/components/landing/testimonials";
import type { TrialOffer } from "@/lib/billing";

/**
 * The video-first "film" landing (`landing_style_v4` treatment; ported from the
 * squidler-film prototype). Cinematic dark hero — drifting aurora blobs, film
 * grain, a rotating "Give it ___" line — with the real product film front and
 * center, an integrations marquee, a "day with your AI agent" timeline, a
 * count-up price reveal, and a quotes carousel. Almost no text.
 *
 * Not a 1:1 port — reworked against WCAG 2.2 AA rather than copied:
 * text on the dark panel is lifted above the prototype's opacities to hold
 * 4.5:1 (3:1 for the large-type dimmed headline) over the shifting aurora;
 * small accent text uses the signal/signal-text pair so it passes in both
 * themes; all interactive elements carry a visible focus ring; the carousel
 * pauses on hover/focus and its dots are ≥24px targets; and the vestibular
 * motion (drifting blobs, entrance rise, play-ring beat, pointer tilt) stops
 * under prefers-reduced-motion. The page's signature *content* animations —
 * word rotation, carousel auto-advance, count-up, logo marquee — run always,
 * per explicit product choice (mirroring the previous landing's marquee):
 * under reduced motion their transitions hard-cut instead of moving.
 *
 * The hero panel is deliberately fixed-palette (deep forest green + brass), the
 * same in light and dark — it's a film frame, not a themed surface. Everything
 * below it uses the app's semantic tokens and follows the site theme. CTAs stay
 * on the brand `signal` tokens.
 */

const HERO_VIDEO_ID = "QJCST-PRI_4";
const HERO_VIDEO_TITLE = msg`Meet Squidler, the agent that knows how you work`;

const ROTATING_WORDS: MessageDescriptor[] = [
  msg`a launch`,
  msg`your inbox`,
  msg`a deadline`,
  msg`a research job`,
  msg`the busywork`,
];

/** Brass accent — the film's secondary metal tone; no site token comes close.
 *  Used only as a surface (chip gradients) under dark espresso text. */
const BRASS = "#c39a6f";

/** Lighter brass for TEXT on the hero panel: ~5.9:1 on the darkest green
 *  (BRASS itself only manages ~4.8:1, too tight once the aurora shifts the
 *  backdrop), so small mono text holds AA everywhere on the panel. */
const BRASS_TEXT = "#d3ac81";

/** Film-grain overlay (fractal noise at 6%) — texture, not content. */
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";


/**
 * CTA with the offer beside it. With a trial: the reference's chip + button
 * row ("TWO WEEKS FREE then …/mo"). Without one there's nothing to announce,
 * so the price becomes an equal-height segment attached flush to the button —
 * `[ from €50/mo ][ Get started free ]` — visible at a glance without reading
 * as a stray tag floating next to the CTA.
 */
function CtaWithOffer({
  ctaHref,
  source,
  trial,
  price,
  dark,
}: {
  ctaHref: string;
  source: string;
  trial: TrialOffer | null;
  price: FilmPrice;
  dark?: boolean;
}) {
  if (trial) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-4">
        <OfferChip trial={trial} price={price} dark={dark} />
        <LandingCta href={ctaHref} source={source} className={ctaButton}>
          <Trans>Get started free</Trans>
        </LandingCta>
      </div>
    );
  }
  return (
    <div className="flex items-stretch justify-center">
      <span
        className={`flex items-center border border-r-0 px-5 text-[1.02rem] ${
          dark ? "border-cream/15 bg-white/5 text-cream/85" : "border-border-subtle bg-foreground/5 text-foreground-muted"
        }`}
      >
        <Trans>
          from <b className={`font-semibold ${dark ? "text-cream" : "text-foreground"}`}>{price.label}</b>
          /mo
        </Trans>
      </span>
      <LandingCta href={ctaHref} source={source} className={ctaButton}>
        <Trans>Get started free</Trans>
      </LandingCta>
    </div>
  );
}

interface FilmPrice {
  /** Numeric sticker amount, e.g. 500 or 50 — what the count-up animates to. */
  amount: number;
  /** Full sticker label in the visitor's geo currency, e.g. "500 SEK" / "$50". */
  label: string;
}

/** "TWO WEEKS FREE" chip + "then from 500 SEK/mo" — the offer in one pill. The
 *  price is the cheapest tier on sale ("from": Max sits above it), and neither
 *  half is hardcoded.
 *  Without a trial there is nothing to announce and a lone price tag next to
 *  the CTA reads as a disconnected fragment — the price moves into the fine
 *  print instead (UnderCta), so this renders nothing. */
function OfferChip({ trial, price, dark }: { trial: TrialOffer | null; price: FilmPrice; dark?: boolean }) {
  if (!trial) return null;
  return (
    <div
      className={`inline-flex items-center gap-2.5 border py-1.5 pl-1.5 pr-4 text-sm ${
        dark ? "border-cream/15 bg-white/5 text-cream/85" : "border-border-subtle bg-foreground/5 text-foreground-muted"
      }`}
    >
      <span
        className="px-3 py-1 font-mono text-[0.7rem] uppercase tracking-[0.03em] text-[#2b1c0e]"
        style={{ background: `linear-gradient(135deg, #A67C52, ${BRASS})` }}
      >
        <Trans>{trial.window} free</Trans>
      </span>
      <span>
        <Trans>
          then from{" "}
          <b className={dark ? "font-semibold text-cream" : "font-semibold text-foreground"}>{price.label}</b>
          /mo
        </Trans>
      </span>
    </div>
  );
}

/** Fine print under a CTA. `brief` is the hero's one-line promise (reference
 *  hero copy); the default spells out the post-trial check-in, deriving its
 *  window and price from the live offer so it can never contradict the chip
 *  above it. */
function UnderCta({
  trial,
  price,
  dark,
  brief,
}: {
  trial: TrialOffer | null;
  price: FilmPrice;
  dark?: boolean;
  brief?: boolean;
}) {
  const { t } = useLingui();
  const copy = !trial
    ? t`Cancel anytime.`
    : brief
      ? t`No card to start. We'll ask before you're ever charged.`
      : t`No card to start. After ${trial.window}, we'll check in — say yes, and plans start at ${price.label}/mo. Say no, and nothing happens.`;
  return (
    <p className={`mt-3.5 text-[0.8rem] ${dark ? "text-cream/70" : "text-foreground-faint"}`}>{copy}</p>
  );
}

/**
 * The "Give it ___." line. The animated word is decorative for assistive tech:
 * a static sr-only sentence carries the meaning, the visible rotation is
 * aria-hidden (a 2.2s live text swap would be noise, not content). Rotates
 * always — it's an opacity crossfade, not movement — per the same product
 * choice as the marquee.
 */
function RotatingLine() {
  const { i18n } = useLingui();
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    const swaps: ReturnType<typeof setTimeout>[] = [];
    const interval = setInterval(() => {
      setShown(false);
      swaps.push(
        setTimeout(() => {
          setIndex((i) => (i + 1) % ROTATING_WORDS.length);
          setShown(true);
        }, 220),
      );
    }, 2200);
    return () => {
      clearInterval(interval);
      swaps.forEach(clearTimeout);
    };
  }, []);

  return (
    <>
      <span className="sr-only">
        <Trans>Give it the busywork.</Trans>
      </span>
      <span aria-hidden="true">
        <Trans>
          Give it{" "}
          <span
            className="border-b-2 border-signal-text pb-px font-semibold text-cream transition-opacity duration-200"
            style={{ opacity: shown ? 1 : 0 }}
          >
            {i18n._(ROTATING_WORDS[index])}
          </span>
          .
        </Trans>
      </span>
    </>
  );
}

/** Scroll-reveal wrapper: fades/rises its children the first time they enter
 *  the viewport. Under reduced motion the content is simply always visible. */
function Reveal({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.16 },
    );
    io.observe(el);
    return () => { io.disconnect(); };
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-[opacity,transform] duration-700 motion-reduce:transition-none ${
        seen ? "opacity-100" : "translate-y-6 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100"
      } ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

/**
 * The product film — a lite-embed façade over youtube-nocookie (same approach
 * and video as the previous landing arms: thumbnail + play ring up front, the
 * iframe mounts on click so the gesture carries autoplay-with-sound and none
 * of YouTube's JS loads until then). Adds the film treatment: a gentle 3D tilt
 * that follows the pointer until playback (skipped under reduced motion).
 */
function FilmVideo() {
  const { t, i18n } = useLingui();
  const [playing, setPlaying] = useState(false);
  const [tilt, setTilt] = useState<{ x: number; y: number } | null>(null);

  const onMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (prefersReducedMotion()) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTilt({ x: (e.clientX - r.left) / r.width - 0.5, y: (e.clientY - r.top) / r.height - 0.5 });
  }, []);

  return (
    <div className="relative z-10 mx-auto -mt-24 max-w-[960px] px-5" style={{ perspective: "1400px" }}>
      <div
        className="relative aspect-video overflow-hidden rounded-[18px] border border-cream/15 bg-black shadow-[0_50px_130px_-40px_rgba(0,0,0,0.55)] transition-transform duration-150 ease-out"
        onMouseMove={playing ? undefined : onMove}
        onMouseLeave={() => { setTilt(null); }}
        style={
          tilt && !playing
            ? { transform: `rotateY(${tilt.x * 7}deg) rotateX(${-tilt.y * 7}deg) scale(1.01)` }
            : undefined
        }
      >
        {playing ? (
          <iframe
            className="size-full border-0"
            src={`https://www.youtube-nocookie.com/embed/${HERO_VIDEO_ID}?autoplay=1&playsinline=1&rel=0`}
            title={i18n._(HERO_VIDEO_TITLE)}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <button
            type="button"
            aria-label={t`Play video: ${i18n._(HERO_VIDEO_TITLE)}`}
            onClick={() => { setPlaying(true); }}
            className={`group relative block size-full cursor-pointer ${focusRing} focus-visible:-outline-offset-4 focus-visible:outline-cream`}
          >
            <Image
              src={`https://i.ytimg.com/vi/${HERO_VIDEO_ID}/maxresdefault.jpg`}
              alt=""
              fill
              priority
              sizes="(max-width: 1000px) 100vw, 960px"
              className="object-cover transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:transition-none"
            />
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[radial-gradient(circle_at_center,rgba(28,47,34,0.05),rgba(28,47,34,0.55))]">
              <span className="grid size-24 animate-[sqBeat_2.4s_ease-in-out_infinite] place-items-center rounded-full bg-gradient-to-br from-signal to-signal-text shadow-[0_0_60px_-4px_rgba(var(--signal-rgb),0.45)] motion-reduce:animate-none">
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="ml-1 size-9 text-cream">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
              <span className="font-mono text-[0.76rem] uppercase tracking-[0.12em] text-cream [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">
                <Trans>Watch it work · 90 sec</Trans>
              </span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

/** Count-up price: 0 → sticker amount over 900ms (cubic ease-out) the first
 *  time it scrolls into view. Runs always — a number changing in place, not
 *  movement — and assistive tech gets only the final price (sr-only below). */
function CountUpPrice({ price }: { price: FilmPrice }) {
  const ref = useRef<HTMLSpanElement>(null);
  const frame = useRef<number>(0);
  const [value, setValue] = useState(0);

  // The label re-assembled around the animated number ("149 SEK" → "" + " SEK",
  // "$14.90" → "$" + ""). If the numeric portion isn't found verbatim, skip the
  // animation rather than display a mangled price.
  const integer = price.amount % 1 === 0;
  const numeric = integer ? String(price.amount) : price.amount.toFixed(2);
  const parts = price.label.split(numeric);
  const countable = parts.length === 2;

  useEffect(() => {
    const el = ref.current;
    if (!el || !countable) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const start = performance.now();
        const step = (ts: number) => {
          const p = Math.min((ts - start) / 900, 1);
          setValue((1 - Math.pow(1 - p, 3)) * price.amount);
          if (p < 1) frame.current = requestAnimationFrame(step);
        };
        frame.current = requestAnimationFrame(step);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(frame.current);
    };
  }, [countable, price.amount]);

  return (
    <p className="font-serif text-[clamp(5rem,16vw,11rem)] leading-[0.9] tracking-[-0.02em] text-foreground-strong">
      {/* Assistive tech gets the final price, not 60 intermediate frames. */}
      <span className="sr-only">
        <Trans>From {price.label}/mo</Trans>
      </span>
      <span aria-hidden="true">
        <small className="align-[0.9em] font-sans text-[0.16em] font-semibold text-foreground-muted">
          <Trans>from</Trans>{" "}
        </small>
        <span ref={ref}>
          {countable ? `${parts[0]}${integer ? Math.round(value) : value.toFixed(2)}${parts[1]}` : price.label}
        </span>
        <small className="align-[0.9em] font-sans text-[0.16em] font-semibold text-foreground-muted">
          {" "}
          <Trans>/mo</Trans>
        </small>
      </span>
    </p>
  );
}

/** A day with it — six moments, revealed in order as the timeline scrolls in. */
const DAY: { icon: LucideIcon; time: MessageDescriptor; title: MessageDescriptor; text: MessageDescriptor }[] = [
  {
    icon: MoonIcon,
    time: msg`06:40 · before you wake`,
    title: msg`Your inbox is already handled`,
    text: msg`42 overnight emails cleared. The 3 that need you, flagged.`,
  },
  {
    icon: SunIcon,
    time: msg`09:00 · morning coffee`,
    title: msg`The day, briefed in one message`,
    text: msg`Calendar and priorities, briefed before your coffee.`,
  },
  {
    icon: EyeIcon,
    time: msg`11:30 · while you're in a meeting`,
    title: msg`It caught the thing you'd have missed`,
    text: msg`Competitor dropped their price. Your reply, drafted and ready.`,
  },
  {
    icon: RocketIcon,
    time: msg`14:00 · after lunch`,
    title: msg`The thing you'd been putting off shipped`,
    text: msg`New pricing page built, tested, sent to you to approve.`,
  },
  {
    icon: SearchIcon,
    time: msg`16:30 · school run`,
    title: msg`The digging is done`,
    text: msg`3 suppliers compared. One page in your inbox.`,
  },
  {
    icon: ClockIcon,
    time: msg`22:00 · you're off the clock`,
    title: msg`It isn't`,
    text: msg`Watching overnight so tomorrow starts already moving.`,
  },
];

/** Carousel quotes: the squidler-film prototype's condensed one-liners of the
 *  full TESTIMONIALS quotes (real customers, used with permission — see
 *  testimonials.tsx). Name/role/photo come from the shared entries so the
 *  identities can't drift apart. */
const QUOTES: { excerpt: MessageDescriptor; from: (typeof TESTIMONIALS)[number] }[] = [
  { excerpt: msg`I wake up to work already done.`, from: TESTIMONIALS[0] },
  { excerpt: msg`I described it out loud. By evening it was built.`, from: TESTIMONIALS[1] },
  { excerpt: msg`It never sleeps, and it never forgets.`, from: TESTIMONIALS[2] },
];

/**
 * Quote carousel. Auto-advances every 4.5s — always, per the marquee's product
 * choice (under reduced motion the slide transition hard-cuts instead of
 * moving) — but never without an out (WCAG 2.2.2): it pauses while hovered or
 * while any dot has focus. The dots are ≥24px targets (2.5.8) with the shared
 * focus ring.
 */
function QuoteCarousel() {
  const { t, i18n } = useLingui();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [epoch, setEpoch] = useState(0); // bumped on manual navigation to restart the timer

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => { setIndex((i) => (i + 1) % QUOTES.length); }, 4500);
    return () => { clearInterval(timer); };
  }, [paused, epoch]);

  return (
    <div
      onMouseEnter={() => { setPaused(true); }}
      onMouseLeave={() => { setPaused(false); }}
      onFocus={() => { setPaused(true); }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
    >
      <div className="relative mx-auto min-h-[230px] max-w-[720px]">
        {QUOTES.map(({ excerpt, from }, i) => (
          <figure
            key={from.name}
            aria-hidden={i !== index}
            className={`absolute inset-0 flex flex-col items-center gap-5 text-center transition-[opacity,transform] duration-500 motion-reduce:transition-none ${
              i === index ? "opacity-100" : "pointer-events-none translate-y-3.5 opacity-0"
            }`}
          >
            <blockquote className="font-serif text-[clamp(1.6rem,4vw,2.4rem)] leading-[1.18] text-foreground-strong">
              &ldquo;{i18n._(excerpt)}&rdquo;
            </blockquote>
            <figcaption className="flex items-center gap-3">
              {from.photo && (
                <Image
                  src={from.photo}
                  alt=""
                  width={48}
                  height={48}
                  className="size-12 rounded-full border-2 border-border-subtle object-cover"
                />
              )}
              <span className="text-left">
                <span className="block text-[0.95rem] font-bold leading-tight text-foreground">{from.name}</span>
                <span className="block text-[0.82rem] text-foreground-faint">{from.role}</span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="mt-4 flex justify-center">
        {QUOTES.map(({ from }, i) => (
          <button
            key={from.name}
            type="button"
            aria-label={t`Show quote from ${from.name}`}
            aria-current={i === index}
            onClick={() => {
              setIndex(i);
              setEpoch((e) => e + 1);
            }}
            className={`group p-2 ${focusRing}`}
          >
            <span
              className={`block h-2 rounded-full transition-all duration-300 motion-reduce:transition-none ${
                i === index ? "w-6 bg-signal" : "w-2 bg-foreground/50 group-hover:bg-foreground/70"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

const SectionEyebrow = ({ children }: { children: ReactNode }) => (
  <p className={`mb-3.5 text-center font-mono text-[0.7rem] uppercase tracking-[0.2em] ${accentText}`}>{children}</p>
);

export function FilmLanding({
  ctaHref,
  trial,
  price,
}: {
  ctaHref: string;
  trial: TrialOffer | null;
  price: FilmPrice;
}) {
  const { i18n } = useLingui();
  return (
    <main className="flex-1">
      {/* HERO — fixed cinematic palette, see the component doc comment.
          pt-16 clears the fixed nav bar so the content centers in the panel's
          VISIBLE area (the reference nav is sticky-in-flow, ours overlays). */}
      <header className="relative flex min-h-[82vh] flex-col overflow-hidden bg-[radial-gradient(140%_120%_at_50%_-10%,#3d5f45,#233A29_52%,#1a2c20)] pt-16 text-cream">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -left-36 -top-52 size-[640px] animate-[sqDrift_22s_ease-in-out_infinite] rounded-full bg-[radial-gradient(circle,#B14A32,transparent_65%)] opacity-55 mix-blend-screen blur-[80px] motion-reduce:animate-none" />
          <div className="absolute -right-44 -top-10 size-[560px] animate-[sqDrift_22s_ease-in-out_-7s_infinite] rounded-full bg-[radial-gradient(circle,#A67C52,transparent_65%)] opacity-55 mix-blend-screen blur-[80px] motion-reduce:animate-none" />
          <div className="absolute -bottom-60 left-1/3 size-[520px] animate-[sqDrift_22s_ease-in-out_-13s_infinite] rounded-full bg-[radial-gradient(circle,#7fae83,transparent_65%)] opacity-40 mix-blend-screen blur-[80px] motion-reduce:animate-none" />
        </div>
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: GRAIN }} />

        {/* True vertical centering (reference: 5rem/3rem padding around the
            centered block); the panel's natural bottom slack absorbs the
            video's -mt-24 overlap. */}
        <div className="relative z-[2] flex flex-1 flex-col items-center justify-center px-5 pb-12 pt-20 text-center">
          {/* Staggered entrance, one delay step per line (reference: .d1–.d5). */}
          <div className="animate-[sqRise_1s_cubic-bezier(0.2,0.7,0.2,1)_0.05s_both] motion-reduce:animate-none">
            <span
              className="inline-flex items-center gap-2 border border-cream/15 bg-white/5 px-3.5 py-1.5 font-mono text-[0.72rem] uppercase tracking-[0.2em]"
              style={{ color: BRASS_TEXT }}
            >
              <span
                aria-hidden="true"
                className="size-[7px] animate-pulse rounded-full motion-reduce:animate-none"
                style={{ background: BRASS_TEXT }}
              />
              <Trans>Meet your Hire</Trans>
            </span>
          </div>
          {/* Deliberately far below the prototype's 12vw/9rem (which maxed out
              already at 1200px): ~94px on a 1440px laptop, 104px cap past
              1600px. */}
          <h1 className="mt-6 animate-[sqRise_1s_cubic-bezier(0.2,0.7,0.2,1)_0.2s_both] font-serif text-[clamp(3rem,6.5vw,6.5rem)] leading-[1.02] tracking-[-0.01em] motion-reduce:animate-none">
            {/* Four fixed rows ("AI." and "agent." on their own) — the
                reference gets this from its 1180px wrap; we pin it so it
                holds at every viewport. */}
            <span className="font-semibold text-cream/70">
              <Trans>
                Don&apos;t prompt an
                <br />
                AI.
              </Trans>
            </span>
            <br />
            <span className="text-cream [text-shadow:0_2px_30px_rgba(247,243,238,0.25)]">
              <Trans>
                Employ an AI
                <br />
                agent.
              </Trans>
            </span>
          </h1>
          <p className="mt-5 min-h-[1.6em] animate-[sqRise_1s_cubic-bezier(0.2,0.7,0.2,1)_0.36s_both] text-[clamp(1.1rem,2.8vw,1.5rem)] text-cream/85 motion-reduce:animate-none">
            <RotatingLine />
          </p>
          {/* Offer + CTA share one row (reference layout); wraps on narrow screens. */}
          <div className="mt-8 animate-[sqRise_1s_cubic-bezier(0.2,0.7,0.2,1)_0.52s_both] motion-reduce:animate-none">
            <CtaWithOffer ctaHref={ctaHref} source="landing-film-hero" trial={trial} price={price} dark />
          </div>
          {/* mb-16 keeps the fine print clear of the video overlapping below:
              when the panel is content-constrained (laptop heights) the slack
              under this line is pb-12 + mb-16 = 112px, minus the video's
              96px -mt overlap → a modest visible gap. */}
          <div className="mb-16 animate-[sqRise_1s_cubic-bezier(0.2,0.7,0.2,1)_0.68s_both] motion-reduce:animate-none">
            <UnderCta trial={trial} price={price} dark brief />
          </div>
        </div>
      </header>

      {/* THE FILM — overlapping the hero's bottom edge. */}
      <FilmVideo />

      {/* WORKS WITH — the previous landing's animated logo rail (pill chips
          auto-scrolling on sqMarquee), then the trust line. Runs always —
          including under prefers-reduced-motion, per explicit product choice
          (carried over from the previous landing) — and pauses on hover. The
          visible copy carries the names, the loop copy is aria-hidden. */}
      <section className="mt-12 border-y border-border-subtle bg-surface-sunken pb-12 pt-10">
        <SectionEyebrow>
          <Trans>Works with</Trans>
        </SectionEyebrow>
        <IntegrationsMarquee className="mt-6" />
        <Reveal className="mx-auto mt-10 max-w-3xl px-5 text-center">
          <p className={`mb-2.5 font-mono text-[0.7rem] uppercase tracking-[0.2em] ${accentText}`}>
            <Trans>Your access, your rules</Trans>
          </p>
          <h2 className="font-serif text-[clamp(1.5rem,3.6vw,2.2rem)] leading-[1.1] text-foreground-strong">
            <Trans>You approve every connection.</Trans>
          </h2>
          <p className="mx-auto mt-2.5 max-w-[52ch] text-foreground-muted">
            <Trans>
              It only sees what you connect — and only does what you allow. Revoke access to any tool in one click, anytime.
            </Trans>
          </p>
        </Reveal>
      </section>

      {/* A DAY WITH IT */}
      <section className="px-5 py-24">
        <SectionEyebrow>
          <Trans>Your day</Trans>
        </SectionEyebrow>
        <h2 className="mb-11 text-center font-serif text-[clamp(2rem,5vw,3.2rem)] text-foreground-strong">
          <Trans>A day with your AI agent.</Trans>
        </h2>
        <ol className="relative mx-auto max-w-[720px] list-none before:absolute before:bottom-3.5 before:left-[23px] before:top-3.5 before:w-0.5 before:bg-gradient-to-b before:from-signal before:via-[#A67C52] before:to-sage before:opacity-35 before:content-['']">
          {DAY.map(({ icon: Icon, time, title, text }, i) => (
            <li key={time.id}>
              <Reveal delay={i * 60} className="relative pb-10 pl-[74px]">
                <span className="absolute left-0 top-0.5 grid size-12 place-items-center rounded-[13px] border border-border-subtle bg-surface-elevated text-foreground-strong shadow-[0_10px_24px_-14px_rgba(0,0,0,0.5)]">
                  <Icon className="size-[23px]" strokeWidth={1.4} aria-hidden="true" />
                </span>
                <p className={`mb-1 font-mono text-[0.7rem] uppercase tracking-[0.14em] ${accentText}`}>{i18n._(time)}</p>
                <h3 className="mb-1 font-serif text-[1.4rem] leading-[1.1] text-foreground-strong">{i18n._(title)}</h3>
                <p className="max-w-[48ch] text-[0.96rem] text-foreground-muted">{i18n._(text)}</p>
              </Reveal>
            </li>
          ))}
        </ol>
      </section>

      {/* ABOUT — one statement. */}
      <section className="border-y border-border-subtle bg-surface-elevated px-5 py-24">
        <SectionEyebrow>
          <Trans>About</Trans>
        </SectionEyebrow>
        <Reveal>
          {/* explicit {" "} after the <em> — the compiler trims the whitespace
              after the closing tag in this multi-line text node, see git blame */}
          <p className="mx-auto max-w-4xl text-center font-serif text-[clamp(1.9rem,4.6vw,3.2rem)] leading-[1.15] text-foreground-strong">
            <Trans>
              Not another chatbot. A colleague that{" "}
              <em className={`italic ${accentText}`}>remembers your business</em>
              {" "}and gets the job done while you&apos;re away.
            </Trans>
          </p>
        </Reveal>
      </section>

      {/* PRICING — the number is the section. */}
      <section className="px-5 py-24 text-center">
        <SectionEyebrow>
          <Trans>Pricing</Trans>
        </SectionEyebrow>
        <CountUpPrice price={price} />
        {trial && (
          <p
            className="mb-7 mt-5 inline-block px-4 py-1.5 font-mono text-[0.8rem] tracking-[0.03em] text-[#2b1c0e]"
            style={{ background: `linear-gradient(135deg, #A67C52, ${BRASS})` }}
          >
            <Trans>{trial.window} free</Trans>
          </p>
        )}
        <div>
          <LandingCta href={ctaHref} source="landing-film-pricing" className={ctaButton}>
            <Trans>Get started free</Trans>
          </LandingCta>
        </div>
        <UnderCta trial={trial} price={price} />
      </section>

      {/* QUOTES */}
      <section className="border-y border-border-subtle bg-surface-elevated px-5 py-24">
        <SectionEyebrow>
          <Trans>From the people who hired one</Trans>
        </SectionEyebrow>
        <QuoteCarousel />
      </section>

      {/* CLOSING */}
      <section className="px-5 py-24">
        <Reveal className="mx-auto max-w-4xl">
          <div className="rounded-[22px] border border-border-subtle bg-[radial-gradient(120%_140%_at_50%_0%,rgba(var(--signal-rgb),0.1),transparent_60%)] px-8 py-16 text-center">
            <h2 className="mb-5 font-serif text-[clamp(2.4rem,6vw,4rem)] text-foreground-strong">
              <Trans>Hand it your first job.</Trans>
            </h2>
            <CtaWithOffer
              ctaHref={ctaHref}
              source="landing-film-final"
              trial={trial}
              price={price}
            />
            <UnderCta trial={trial} price={price} />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
